import { Fragment } from 'react';
import { Link, useLocation } from 'react-router';
import { MouseEventHandler, ReactNode, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getPlatformVersionLabel } from '../../utils/platformVersion';
import { openChangelogDialog } from '../ui/ChangelogDialog';
import { StageBadge } from '../ui/StageBadge';
import type { EnvStage } from '../../features';
export interface NavbarProps {
    items: NavbarItems[];
    MiddleItemContainer?: React.ComponentType<any>|undefined,
    footerItems: NavbarItems[];
    // icon: string|null;
    // iconClass: string;
    navbarClass: string;
};
export interface NavbarItems {
    name: string;
    // i18n (Task #76): optionaler Übersetzungsschlüssel im common-Namespace. Wenn gesetzt, rendert
    // die Sidebar t(labelKey)/t(descKey) statt name/description; sonst bleibt name/description der
    // Fallback. So bleiben die Items reine Daten und die Übersetzung passiert in der Komponente.
    labelKey?: string;
    descKey?: string;
    href: string;
    icon?: ReactNode;
    iconClass?: string;
    description?: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
    class: string;
    subSection?: SubClassProps[];
    pipeline?: string;
    chat?: string;
    handleClick?: MouseEventHandler<HTMLAnchorElement>;
    dividerAfter?: boolean;
    highlight?: boolean;
    // Stage-Markierung (StageBadge): hoechste Umgebung, in der der Eintrag erscheint.
    maxStage?: EnvStage;
}
export interface SubClassProps {
    name: string;
    labelKey?: string;
    href: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
    class: string;
    icon?: ReactNode;
    iconClass?: string;
    pipeline?: string;
    chat?: string;
    handleClick?: MouseEventHandler<HTMLAnchorElement>;
};

export interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  navbarProps: NavbarProps;
  onClose: () => void;
  onToggleText: () => void;
}

export function Sidebar({ isOpen, isCollapsed: isCollapsedProp, navbarProps, onClose, onToggleText }: SidebarProps) {
  const location = useLocation();
  const { effectiveTheme } = useBootstrapTheme();
  const isMobile = useIsMobile();
  // On mobile the sidebar only ever renders as a full drawer (opened via the topbar hamburger,
  // see PageMaster.tsx) - "collapsed" (icon-only) is a desktop-only concept and would otherwise
  // shrink that drawer to an unusable 64px icon strip, since sidebarCollapsed defaults to true
  // and PageMaster forces it back to true on every mobile resize.
  const isCollapsed = isMobile ? false : isCollapsedProp;
  const { t } = useTranslation('common');
  // Items einmal lokalisieren (name/description -> t(labelKey/descKey), sonst Fallback). Danach
  // bleiben alle Render-Stellen unveraendert - sie lesen einfach das uebersetzte name/description.
  const localizeItem = (item: NavbarItems): NavbarItems => ({
    ...item,
    name: item.labelKey ? t(item.labelKey) : item.name,
    description: item.descKey ? t(item.descKey) : item.description,
    subSection: item.subSection?.map(sub => ({
      ...sub,
      name: sub.labelKey ? t(sub.labelKey) : sub.name,
    })),
  });
  const sidebarItems = navbarProps.items.map(localizeItem);
  const footerItems = navbarProps.footerItems.map(localizeItem);
  const platformVersionLabel = getPlatformVersionLabel();
  const [dropdownStates, setDropdownStates] = useState<Record<string, boolean>>(
        () => Object.fromEntries(sidebarItems.map(item => [item.name, !!(item.subSection && item.subSection.length > 0)]))
  );
  const MiddleItemContainer: React.ComponentType<any>|undefined = navbarProps.MiddleItemContainer; 
  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && isMobile && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
          }}
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div
        style={{
          position: 'fixed',
          top: '64px',
          // bottom statt height: calc(100vh - 64px) - auf Mobile ist 100vh hoeher als die sichtbare
          // Flaeche, der Sidebar-Fuss (Rechtliches, Version) lag sonst unter der Adressleiste.
          bottom: 0,
          left: 0,
          width: isCollapsed ? '64px' : isMobile ? 'min(85vw, 256px)' : '256px',
          zIndex: 1000,
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease-in-out, width 0.3s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: effectiveTheme === 'dark'
            ? 'linear-gradient(180deg, rgba(20, 38, 80, 0.9) 0%, rgba(15, 23, 42, 0.95) 50%, rgba(30, 41, 59, 0.9) 100%)'
            : 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 50%, rgba(241, 245, 249, 0.95) 100%)',
          backdropFilter: 'blur(12px)',
          borderRight: effectiveTheme === 'dark'
            ? '1px solid rgba(96, 165, 250, 0.2)'
            : '1px solid rgba(59, 130, 246, 0.1)',
          boxShadow: effectiveTheme === 'dark'
            ? '4px 0 20px rgba(59, 130, 246, 0.1)'
            : '4px 0 20px rgba(59, 130, 246, 0.05)'
        }}
      >
        {/* Sidebar header - mobile close button only */}
        {isMobile && (
          <div 
            className="border-bottom"
            style={{
              height: '64px',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexShrink: 0
            }}
          >
            {/* Close button for mobile */}
            {!isCollapsed && (
              <button
                className="btn btn-sm btn-link text-muted p-1"
                onClick={onClose}
                title={t('nav.hideSidebar')}
              >
                ×
              </button>
            )}
          </div>
        )}
        
        {/* Navigation */}
        <nav 
          style={{
            flex: 1,
            padding: isCollapsed ? '24px 8px' : '24px 16px',
            overflowY: 'auto'
          }}
          className={navbarProps.navbarClass}
        >
          <ul className="navbar-nav">
            {sidebarItems.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/dashboard' && item.href !== '/analytics' && location.pathname.startsWith(item.href))
                || (item.subSection && item.subSection.map(x => x.href))?.includes(location.pathname);
              const isOpen = dropdownStates[item.name] ?? false;
              return (
                <Fragment key={item.name}>
                { item.subSection && item.subSection.length > 0 && !isCollapsed
                  ? <li className={item.class ?? ''} data-tour={`nav-${item.href}`}>
                      <div className={`btn-group d-block rounded-2 nav-item-hover ${
                              isActive ? 'bg-primary bg-opacity-10 border border-primary border-opacity-25 text-primary' : 
                              'text-body-secondary border border-transparent'
                            }`}
                          key={item.name}
                        style={{
                          padding: isCollapsed ? '12px 8px' : '12px',
                          justifyContent: isCollapsed ? 'center' : 'flex-start',
                          position: 'relative',
                          cursor: 'pointer',
                          background: isActive
                            ? effectiveTheme === 'dark'
                              ? 'linear-gradient(135deg, rgba(96, 165, 250, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%)'
                            : 'transparent',
                          border: isActive
                            ? effectiveTheme === 'dark'
                              ? '1px solid rgba(96, 165, 250, 0.3)'
                              : '1px solid rgba(59, 130, 246, 0.2)'
                            : '1px solid transparent',
                          color: isActive
                            ? effectiveTheme === 'dark' ? '#60a5fa' : '#3b82f6'
                            : effectiveTheme === 'dark' ? '#cbd5e1' : '#64748b',
                          transition: 'all 0.2s ease',
                          boxShadow: isActive
                            ? effectiveTheme === 'dark'
                              ? '0 4px 12px rgba(96, 165, 250, 0.1)'
                              : '0 4px 12px rgba(59, 130, 246, 0.08)'
                            : 'none'
                        }}
                        onClick={() => {
                            setDropdownStates((prev) => ({
                            ...prev,
                            [item.name]: !prev[item.name],
                            }));
                            if (isMobile){
                              onClose();
                            }
                        }}
                        >
                        <div className="d-flex align-items-center justify-content-between">
                          <a
                            className={`nav-link d-flex align-items-center text-decoration-none mb-2 nav-sub-item-hover ${
                              isActive ? 'text-primary' : 
                              'text-body-secondary'
                            }`}
                            style={{
                              color: isActive
                              ? effectiveTheme === 'dark' ? '#60a5fa' : '#3b82f6'
                              : effectiveTheme === 'dark' ? '#cbd5e1' : '#64748b',
                            }}
                            href={item.href ?? "#"}
                          >
                            {isCollapsed ? item.name : ''}
                            {/* Icon container with fixed size */}
                            <div 
                              style={{
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: isCollapsed ? '0' : '12px',
                                flexShrink: 0
                              }}
                            >
                              {item.icon}
                            </div>
                            {/* Text content - only show when not collapsed */}
                            {!isCollapsed && (
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="fw-medium mb-1" title={item.description}>
                                  {item.name}
                                </div>
                                {/* <div className="small text-muted text-truncate">
                                  {item.description}
                                </div> */}
                              </div>
                            )}
                          </a>
                        </div>
                        <ul className={`bg-transparent border-0 mt-0 pt-1 pb-0 mx-auto ${
                            isOpen ? "d-block" : "d-none"
                          }`}
                          style={{ listStyle: "none", paddingLeft:"44px"}}
                        >
                          {item.subSection.map((element)=>(
                              <li
                                className={element.class}
                                key={element.name}
                              >
                                <a
                                  className="nav-link text-decoration-none p-0 nav-sub-item-hover"
                                  href={element.href ?? "#"}
                                  onClick={() => {
                                    element.onClick;
                                  }}
                                  style={{
                                    color: location.pathname === element.href
                                    ? effectiveTheme === 'dark' ? '#60a5fa' : '#3b82f6'
                                    : effectiveTheme === 'dark' ? '#cbd5e1' : '#64748b',
                                  }}
                                >
                                  {element.name}
                                </a>
                              </li>
                          ))}
                        </ul>
                      </div>  
                    </li>
                  : <li className={item.class ?? ''} data-tour={`nav-${item.href}`}>
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`nav-link d-flex align-items-center text-decoration-none mb-2 rounded-2 nav-item-hover ${
                          isActive ? 'bg-primary bg-opacity-10 border border-primary border-opacity-25 text-primary' :
                          'text-body-secondary border border-transparent'
                        }`}
                        style={{
                          padding: isCollapsed ? '12px 8px' : '12px',
                          justifyContent: isCollapsed ? 'center' : 'flex-start',
                          position: 'relative',
                          background: isActive
                            ? effectiveTheme === 'dark'
                              ? 'linear-gradient(135deg, rgba(96, 165, 250, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%)'
                            : item.highlight
                              ? effectiveTheme === 'dark'
                                ? 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(5,150,105,0.08) 100%)'
                                : 'linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(5,150,105,0.05) 100%)'
                              : 'transparent',
                          border: isActive
                            ? effectiveTheme === 'dark'
                              ? '1px solid rgba(96, 165, 250, 0.3)'
                              : '1px solid rgba(59, 130, 246, 0.2)'
                            : '1px solid transparent',
                          color: isActive
                            ? effectiveTheme === 'dark' ? '#60a5fa' : '#3b82f6'
                            : effectiveTheme === 'dark' ? '#cbd5e1' : '#64748b',
                          transition: 'all 0.2s ease',
                          boxShadow: isActive
                            ? effectiveTheme === 'dark'
                              ? '0 4px 12px rgba(96, 165, 250, 0.1)'
                              : '0 4px 12px rgba(59, 130, 246, 0.08)'
                            : 'none'
                        }}
                        title={isCollapsed ? item.name : ''}
                        onClick={() => {
                          // Close sidebar on mobile when navigating
                          if (isMobile) {
                            onClose();
                          }
                        }}
                      >
                        {/* Icon container with fixed size */}
                        <div 
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: isCollapsed ? '0' : '12px',
                            flexShrink: 0
                          }}
                        >
                          <span 
                            style={{ 
                              fontSize: '18px',
                              lineHeight: 1
                            }}
                          >
                            {item.icon}
                          </span>
                        </div>
                        {isCollapsed && <StageBadge maxStage={item.maxStage} compact className="" />}
                        {/* Text content - only show when not collapsed */}
                        {!isCollapsed && (
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="fw-medium mb-1" title={item.description}>
                              {item.name}
                              <StageBadge maxStage={item.maxStage} />
                            </div>
                            {/* <div className="small text-muted text-truncate">
                              {item.description}
                            </div> */}
                          </div>
                        )}
                      </Link>
                    </li>
                  }
                  {item.dividerAfter && (
                    <li aria-hidden="true" style={{
                      height: '1px',
                      margin: '8px 0',
                      background: effectiveTheme === 'dark'
                        ? 'rgba(96,165,250,0.12)'
                        : 'rgba(0,0,0,0.08)',
                    }} />
                  )}
                </Fragment>
              );
            })}
          </ul>
          {MiddleItemContainer ?
            <MiddleItemContainer isCollapsed={isCollapsed} />
            : <></>
          }
          {/*Footer
            Legacy Links should be shown as icon when bar is collapsed and shown as simple links if sidebar is not collapsed
          */}
          <div 
            className="border-top pt-3 mt-auto"
            style={{ flexShrink: 0 }}
          >
            <div className="small text-muted text-center">
              <ul className="navbar-nav">
                {footerItems.map((item)=>{
                  const isActive = location.pathname === item.href || 
                  (item.href !== '/dashboard' && location.pathname.startsWith(item.href))
                  || (item.subSection && item.subSection.map(x => x.href))?.includes(location.pathname);
                  return(
                    <Fragment key={item.name}>
                      {!isCollapsed
                        ?
                          <>
                            {item.subSection && item.subSection.length > 0 
                              ? 
                                <ul className="navbar-nav">
                                  {item.subSection.map((element)=>(
                                      <li className={element.class} key={element.name}>
                                        <a
                                          className={`nav-link align-items-center text-decoration-none mb-2 nav-footer-hover ${
                                            location.pathname === element.href ? 'text-primary' :
                                            'text-body-secondary'
                                          }`}
                                          href={element.href ?? "#"}
                                          onClick={element.onClick}
                                        >
                                          {element.name}
                                        </a>
                                      </li>
                                  ))}
                                </ul>
                              : 
                                <li className={item.class}>
                                  <a 
                                    className= {`nav-link align-items-center text-decoration-none mb-2 nav-footer-hover ${
                                  location.pathname === item.href ? 'text-primary' : 
                                  'text-body-secondary'
                                }`}
                                    href={item.href ?? "#"} 
                                    onClick={item.onClick}
                                  >
                                    {item.name}
                                  </a>
                                </li>
                            }
                            <li>
                              <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none"
                                style={{ fontSize: '11px', color: effectiveTheme === 'dark' ? '#94a3b8' : '#6b7280' }}
                                onClick={() => openChangelogDialog('all')}
                                title={platformVersionLabel}
                                data-tour="platform-version"
                              >
                                SengiLabs {platformVersionLabel}
                                <span
                                  className="badge rounded-pill ms-1"
                                  style={{ fontSize: '9px', backgroundColor: effectiveTheme === 'dark' ? '#334155' : '#e5e7eb', color: effectiveTheme === 'dark' ? '#94a3b8' : '#6b7280' }}
                                >
                                  {t('beta.badge')}
                                </span>
                              </button>
                            </li>
                          </>
                        :
                          <li className={item.class ?? ''} data-tour={`nav-${item.href}`}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', padding: '4px 0' }}>
                              {(item.subSection ?? []).map(sub => (
                                <a
                                  key={sub.name}
                                  className="nav-footer-hover"
                                  href={sub.href}
                                  title={sub.name}
                                  style={{
                                    fontSize: '10px',
                                    lineHeight: 1.2,
                                    textDecoration: 'none',
                                    color: location.pathname === sub.href
                                      ? (effectiveTheme === 'dark' ? '#60a5fa' : '#3b82f6')
                                      : (effectiveTheme === 'dark' ? '#94a3b8' : '#6b7280'),
                                    textAlign: 'center',
                                    maxWidth: '56px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    display: 'block',
                                  }}
                                >
                                  {sub.name}
                                </a>
                              ))}
                              <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none"
                                style={{
                                  fontSize: '9px',
                                  marginTop: '8px',
                                  color: effectiveTheme === 'dark' ? '#94a3b8' : '#6b7280',
                                  textAlign: 'center',
                                }}
                                onClick={() => openChangelogDialog('all')}
                                title={platformVersionLabel}
                                data-tour="platform-version"
                              >
                                {platformVersionLabel} · {t('beta.badge')}
                              </button>
                            </div>
                          </li>
                      }
                    </Fragment>
                  )})}
                </ul>
              </div>
            </div>
        </nav>
      </div>
    </>
  );
}